import Card from "../UI/Card";
import { Argent, DG, Interrogation } from "../UI/Icons";
import { useState } from "react";
import Tooltip from "../UI/Tooltip";
import {
  classnames,
  setBalanceAmountSignFromType,
} from "../../utils/index.jsx";
import { currencyFormatter } from "../../api/Functions";
import {
  useBalanceTypes,
  useTenantAccountBalance,
  useTenantBalances,
} from "../../hooks/use-balances";
import IconAndText from "../UI/IconAndText";
import useFiles from "../../hooks/use-files";
import BalanceForm from "../Balance/BalanceForm";
import { sanitizeValues } from "../../models/utils";
import BalanceList from "../Balance/BalanceList";
import { BalanceDomain } from "../../utils/constants/balances";
import Button from "../UI/Button";

export default function TenantBalance({ tenant }) {
  const {
    balances,
    deleteBalance,
    updateBalance,
    createBalance,
    duplicateBalance,
  } = useTenantBalances(tenant?.id, true);
  const { balances: balanceTenant, mutate: refreshBalanceTenant } =
    useTenantAccountBalance(tenant?.id, true);
  const [balanceToEdit, setBalanceToEdit] = useState();
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [showBalance, setShowBalance] = useState(false);

  const { balanceTypesByCategory, isLoading } = useBalanceTypes();
  const { files } = useFiles({ tenantId: tenant?.id });
  if (isLoading || !balanceTenant) return null;

  const quittances = files.filter((filtered) => filtered.tag === "quittance");

  async function submitBalanceHandler(edit, values) {
    const sanitizedValues = { ...sanitizeValues(values) };
    // Set correct sign based on balance type
    sanitizedValues.montant = setBalanceAmountSignFromType(
      sanitizedValues,
      balanceTypesByCategory,
      sanitizedValues.is_remboursement,
    );
    delete sanitizedValues.is_remboursement;

    if (edit) {
      await updateBalance(balanceToEdit.id, sanitizedValues);
    } else {
      await createBalance(sanitizedValues);
    }
    await refreshBalanceTenant();
    setIsBalanceModalOpen(false);
  }

  return (
    <>
      <BalanceForm
        balance={balanceToEdit}
        domaineBalance={BalanceDomain.TENANT}
        parentId={tenant?.id}
        open={isBalanceModalOpen}
        onOpenChange={(open) => {
          setIsBalanceModalOpen(open);
          if (!open) setBalanceToEdit(null);
        }}
        onSubmit={(values) =>
          submitBalanceHandler(balanceToEdit !== undefined, values)
        }
        onDelete={async (id) => {
          try {
            await deleteBalance(id);
            setIsBalanceModalOpen(false);
          } catch (error) {
            console.error(error);
          }
        }}
      />
      <div className={"flex wrap-reverse gap10 marginB10"}>
        <Card className={"flex-grow flex padding10 align-items-center"}>
          <div className={"center text-center"}>
            <IconAndText
              icon={<Argent />}
              text={<p className={"primaryText"}>Solde du compte locataire</p>}
            />
            <p
              className={classnames(
                "primaryText",
                balanceTenant.balance < 0 ? "error" : "green",
              )}
            >
              {currencyFormatter(balanceTenant.balance) ?? "0€"}
            </p>
          </div>
        </Card>
        <Card className={"flex-grow flex padding10 align-items-center"}>
          <div className={"center text-center"}>
            <IconAndText
              icon={<DG />}
              text={<p className={"primaryText"}>Dépôt de garantie encaissé</p>}
            />
            <div
              className={classnames(
                "primaryText",
                balanceTenant.dg_debit + balanceTenant.dg_credit < 0
                  ? "error"
                  : "green",
              )}
            >
              {currencyFormatter(balanceTenant.dg_debit)}/
              {currencyFormatter(-balanceTenant.dg_credit)}{" "}
              <Tooltip
                content={"Dépôt de garantie perçu / dépôt de garantie appelé"}
              >
                <Interrogation />
              </Tooltip>{" "}
            </div>
          </div>
        </Card>
      </div>
      <div className={"text-center marginB10"}>
        {showBalance ? (
          <Button
            variant="tertiary"
            onClick={() => setShowBalance((prev) => !prev)}
          >
            Masquer le détail de la balance
          </Button>
        ) : (
          <Button
            variant="tertiary"
            onClick={() => setShowBalance((prev) => !prev)}
          >
            Afficher le détail de la balance
          </Button>
        )}
      </div>

      {showBalance && (
        <BalanceList
          balances={balances}
          quittances={quittances}
          isTenant
          isLocataire={true}
          onUpdate={(balance) => {
            setBalanceToEdit(balance);
            setIsBalanceModalOpen(true);
          }}
          onDuplicate={async (balanceId) => {
            await duplicateBalance(balanceId);
            await refreshBalanceTenant();
          }}
          onCreate={async (balance) => {
            await createBalance(balance);
            await refreshBalanceTenant();
          }}
          onDelete={async (balanceId) => {
            await deleteBalance(balanceId);
            await refreshBalanceTenant();
          }}
        />
      )}
    </>
  );
}
